import { useTranslation } from 'react-i18next';

import { Form, Formik } from 'formik';

import { DateInput, DigitalSignageTimePicker } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { dateFormat } from '@/constants';
import { TestIds } from '@/shared/test-ids';
import { TimeSlotFormValues, UpdateTimeSlotPayload } from '@/types';

type TimeSlotModalProps = {
  date: Date | null;
  onClose: () => void;
  onAssign?: (data: TimeSlotFormValues) => void;
  deviceSchedule?: {
    id: number;
    start: string | Date;
    end: string | Date;
    allDay: boolean;
  };
  onEdit?: (data: UpdateTimeSlotPayload) => void;
};

export const TimeSlotModal = ({
  date,
  onClose,
  onAssign,
  deviceSchedule,
  onEdit
}: TimeSlotModalProps) => {
  const [t] = useTranslation('common');
  const options = ['yes', 'no'];

  let initialDate = date;
  let initialAllDay = false;
  let initialStartTime = '';
  let initialEndTime = '';

  const pad = (num: number) => num.toString().padStart(2, '0');

  if (deviceSchedule) {
    const start = new Date(deviceSchedule.start);
    const end = new Date(deviceSchedule.end);
    initialDate = start;
    initialAllDay = deviceSchedule.allDay;
    initialStartTime = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
    initialEndTime = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
  } else if (date) {
    const startHours = pad(date.getHours());
    const startMinutes = pad(date.getMinutes());
    const endDate = new Date(date.getTime() + 30 * 60000);
    const endHours = pad(endDate.getHours());
    const endMinutes = pad(endDate.getMinutes());
    initialStartTime = `${startHours}:${startMinutes}`;
    initialEndTime = `${endHours}:${endMinutes}`;
  }

  const initialValues = {
    date: initialDate,
    allDay: initialAllDay,
    startTime: initialStartTime,
    endTime: initialEndTime
  };

  if (!initialDate) {
    return null;
  }

  const isEditMode = !!deviceSchedule;

  return (
    <div
      data-testid={TestIds.digitalSignage.timeSlotModal}
      className={`absolute ${deviceSchedule ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : 'left-full top-5'} z-50 h-auto w-[350px] rounded-md bg-white p-4 shadow-[0_8px_16px_0_#2849867A]`}>
      <Label
        text={isEditMode ? t('editSlot') : t('addNewSlot')}
        type={LABEL_TYPE.H3}
        className='!text-primary-blue'
      />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => {
          if (isEditMode && onEdit) {
            onEdit({ ...values, id: deviceSchedule?.id });
          } else if (onAssign) {
            onAssign(values);
          }
        }}>
        {({ values, setFieldValue }) => (
          <Form className='mt-2'>
            <div className='my-2 flex w-full flex-row gap-x-2'>
              <div data-testid={TestIds.digitalSignage.timeSlotDate} className='w-full'>
                <DateInput
                  id='date'
                  label={t('Date')}
                  selected={values.date}
                  onChange={(date: Date) => setFieldValue('date', date)}
                  isRequired={false}
                  dateFormat={dateFormat}
                />
              </div>
              <div className='flex h-16 flex-col gap-y-2'>
                <Label text={t('allDay')} type={LABEL_TYPE.PARAGRAPH2} />
                <div className='flex justify-between gap-x-1 rounded-[10px] border border-secondary-blue-gray p-1'>
                  {options.map((option) => (
                    <Button
                      dataTestId={
                        option === 'yes'
                          ? TestIds.digitalSignage.timeSlotAllDayYes
                          : TestIds.digitalSignage.timeSlotAllDayNo
                      }
                      key={option}
                      handleOnClick={() => setFieldValue('allDay', option === 'yes')}
                      className={`z-10 h-8 w-1/2 ${
                        (option === 'yes') !== values.allDay &&
                        'bg-secondary-blue-gray text-secondary-gray hover:text-primary-black'
                      }`}>
                      {t(option)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {!values.allDay && (
              <div className='my-3 flex w-full flex-row gap-2'>
                <div
                  data-testid={TestIds.digitalSignage.timeSlotStart}
                  className='flex w-full flex-col gap-y-2'>
                  <Label text={t('startTime')} type={LABEL_TYPE.PARAGRAPH2} />
                  <DigitalSignageTimePicker
                    value={values.startTime}
                    onChange={(val: string) => {
                      setFieldValue('startTime', val);

                      const [startHStr, startMStr] = val.split(':');
                      const startH = Number(startHStr);
                      const startM = Number(startMStr);
                      const [endHStr, endMStr] = values.endTime.split(':');
                      const endH = Number(endHStr);
                      const endM = Number(endMStr);

                      const now = new Date();
                      const startDate = new Date(now);
                      startDate.setHours(startH, startM, 0, 0);

                      const endDate = new Date(now);
                      endDate.setHours(endH, endM, 0, 0);

                      if (startDate >= endDate) {
                        const newEndDate = new Date(startDate.getTime() + 30 * 60000);
                        const newEndH = newEndDate.getHours().toString().padStart(2, '0');
                        const newEndM = newEndDate.getMinutes().toString().padStart(2, '0');
                        setFieldValue('endTime', `${newEndH}:${newEndM}`);
                      }
                    }}
                  />
                </div>
                <div
                  data-testid={TestIds.digitalSignage.timeSlotEnd}
                  className='flex w-full flex-col gap-y-2'>
                  <Label text={t('endTime')} type={LABEL_TYPE.PARAGRAPH2} />
                  <DigitalSignageTimePicker
                    value={values.endTime}
                    onChange={(val: string) => {
                      setFieldValue('endTime', val);

                      const [startHStr, startMStr] = values.startTime.split(':');
                      const startH = Number(startHStr);
                      const startM = Number(startMStr);
                      const [endHStr, endMStr] = val.split(':');
                      const endH = Number(endHStr);
                      const endM = Number(endMStr);

                      const now = new Date();
                      const startDate = new Date(now);
                      startDate.setHours(startH, startM, 0, 0);

                      const endDate = new Date(now);
                      endDate.setHours(endH, endM, 0, 0);

                      if (endDate <= startDate) {
                        const newStartDate = new Date(endDate.getTime() - 30 * 60000);
                        const newStartH = newStartDate.getHours().toString().padStart(2, '0');
                        const newStartM = newStartDate.getMinutes().toString().padStart(2, '0');
                        setFieldValue('startTime', `${newStartH}:${newStartM}`);
                      }
                    }}
                  />
                </div>
              </div>
            )}

            <div className='mt-6 flex w-full flex-row gap-x-2'>
              <Button
                dataTestId={TestIds.digitalSignage.timeSlotCancel}
                type='button'
                variant={ButtonVariant.SECONDARY}
                handleOnClick={onClose}
                className='w-full'>
                {t('cancel')}
              </Button>
              <Button
                dataTestId={TestIds.digitalSignage.timeSlotSubmit}
                type='submit'
                variant={ButtonVariant.PRIMARY}
                className='w-full'>
                {isEditMode ? t('edit') : t('add')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
